/**
 * CompletePage — standalone post-completion route at `/complete/:id`.
 *
 * Sprint 11.3 redesign: ruhige confirmation-receipt layout with a brand
 * strip on top, the redesigned SessionComplete card as body, and a footer
 * crumb. Loading state stacks three Skeleton cards; errors render via
 * ErrorPill with retry.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import type { InterviewSession } from '@shared/types.ts';
import { getSession, getTranscript, restartInterview } from '../services/api';
import { SessionComplete } from './SessionComplete';
import type { TranscriptViewProps } from './TranscriptView';
import { KaivaIcon, KaivaWordmark } from './brand';
import { ErrorPill, Skeleton } from './feedback';

const DEMO_FALLBACK_ID = 'demo';

interface CompleteLocationState {
  sessionName?: string;
  durationSeconds?: number;
  startedAt?: string | Date | null;
  completedAt?: string | Date | null;
  transcript?: TranscriptViewProps['transcript'];
}

type SessionDetails = InterviewSession & {
  sessionName: string;
  startedAt?: string | Date | null;
  completedAt?: string | Date | null;
};

function toErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return 'An unexpected error occurred while loading completion details.';
}

function normalizeTranscriptContent(content: unknown): TranscriptViewProps['transcript'] {
  if (Array.isArray(content)) {
    return content as TranscriptViewProps['transcript'];
  }
  return null;
}

export function CompletePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = (location.state as CompleteLocationState | null) ?? null;

  const [session, setSession] = useState<SessionDetails | null>(null);
  const [savedTranscript, setSavedTranscript] = useState<TranscriptViewProps['transcript']>(
    locationState?.transcript ?? null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isRestarting, setIsRestarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadCompletionData = useCallback(async () => {
    if (!id) {
      setError('Session ID is missing from the URL.');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    const [sessionResult, transcriptResult] = await Promise.allSettled([
      getSession(id),
      getTranscript(id),
    ]);

    if (sessionResult.status === 'fulfilled') {
      setSession(sessionResult.value.session as SessionDetails);
    } else {
      setError(toErrorMessage(sessionResult.reason));
    }

    if (transcriptResult.status === 'fulfilled') {
      const normalized = normalizeTranscriptContent(
        transcriptResult.value.transcript.content,
      );
      if (normalized && normalized.length > 0) {
        setSavedTranscript(normalized);
      } else if (transcriptResult.value.transcript.rawText) {
        setSavedTranscript([
          {
            role: 'system',
            text: transcriptResult.value.transcript.rawText,
            timestamp: transcriptResult.value.transcript.createdAt,
          },
        ]);
      }
    }

    setIsLoading(false);
  }, [id]);

  useEffect(() => {
    let mounted = true;
    void loadCompletionData().then(() => {
      if (!mounted) return;
    });
    return () => {
      mounted = false;
    };
  }, [loadCompletionData]);

  const transcript = useMemo(
    () => savedTranscript ?? locationState?.transcript ?? null,
    [locationState?.transcript, savedTranscript],
  );

  const sessionName = session?.sessionName || locationState?.sessionName || 'Interview Session';
  const sessionIdShort = id ? id.slice(0, 8) : '';

  return (
    <main
      className="flex min-h-screen flex-col"
      style={{
        background: 'hsl(var(--background))',
        color: 'hsl(var(--foreground))',
      }}
    >
      {/* Brand strip */}
      <header
        className="px-4 py-4 sm:px-6"
        style={{ borderBottom: '1px solid hsl(var(--border))' }}
      >
        <div className="mx-auto flex w-full items-center gap-3" style={{ maxWidth: 720 }}>
          <KaivaIcon size={28} />
          <KaivaWordmark size={17} tone="ink" />
          <span
            className="ml-2 kv-eyebrow"
            style={{
              color: 'hsl(var(--muted-foreground))',
            }}
          >
            Workspace · {sessionName}
          </span>
        </div>
      </header>

      {/* Body — text-heavy receipt, carries the whisper substrate */}
      <div className="relative flex-1">
        <div className="kv-substrate kv-substrate--whisper" aria-hidden="true" />
      <div
        className="relative z-10 mx-auto w-full px-4 sm:px-6"
        style={{ maxWidth: 720, paddingTop: 64, paddingBottom: 32 }}
      >
        {isLoading ? (
          <LoadingState />
        ) : !session ? (
          <ErrorState message={error} onRetry={loadCompletionData} fallbackId={id ?? DEMO_FALLBACK_ID} />
        ) : (
          <>
            {error && (
              <div className="mb-4">
                <ErrorPill message={error} onRetry={loadCompletionData} />
              </div>
            )}
            <SessionComplete
              sessionName={sessionName}
              durationSeconds={session.duration ?? locationState?.durationSeconds ?? 0}
              startedAt={session.startedAt ?? locationState?.startedAt}
              completedAt={session.completedAt ?? locationState?.completedAt}
              transcript={transcript}
              dashboardHref="/"
              isRestarting={isRestarting}
              onStartNewSession={async () => {
                if (!id || isRestarting) return;
                setIsRestarting(true);
                setError(null);
                try {
                  await restartInterview(id);
                  navigate(`/session/${id}`);
                } catch (err) {
                  setError(toErrorMessage(err));
                  setIsRestarting(false);
                }
              }}
            />
          </>
        )}

        {/* Footer crumb */}
        <footer
          className="mt-8 flex items-center justify-between gap-3"
          style={{
            paddingTop: 16,
            borderTop: '1px solid hsl(var(--border))',
          }}
        >
          <Link
            to="/"
            className="kv-eyebrow transition-colors hover:[color:hsl(var(--sienna-deep))]"
            style={{
              color: 'hsl(var(--muted-foreground))',
              textDecoration: 'none',
            }}
          >
            ← Back to home
          </Link>
          {sessionIdShort && (
            <span
              style={{
                fontSize: 10.5,
                letterSpacing: '0.04em',
                color: 'hsl(var(--muted-foreground))',
              }}
            >
              session #{sessionIdShort}…
            </span>
          )}
        </footer>
      </div>
      </div>
    </main>
  );
}

function LoadingState() {
  return (
    <div className="flex flex-col gap-3" aria-busy="true" aria-live="polite">
      <Skeleton variant="card" />
      <Skeleton variant="card" />
      <Skeleton variant="card" />
    </div>
  );
}

function ErrorState({
  message,
  onRetry,
  fallbackId,
}: {
  message: string | null;
  onRetry: () => void;
  fallbackId: string;
}) {
  return (
    <div className="flex flex-col gap-4">
      <ErrorPill
        message={message ?? 'Unable to load this completed session.'}
        onRetry={onRetry}
      />
      <div className="flex flex-wrap gap-2">
        <Link
          to={`/session/${fallbackId}`}
          className="inline-flex items-center transition-colors hover:[background:hsl(var(--muted))]"
          style={{
            height: 32,
            padding: '0 12px',
            borderRadius: 8,
            background: 'hsl(var(--card))',
            color: 'hsl(var(--foreground))',
            border: '1px solid hsl(var(--border))',
            fontSize: 12.5,
            fontWeight: 500,
            textDecoration: 'none',
          }}
        >
          Back to session
        </Link>
        <Link
          to="/"
          className="inline-flex items-center transition-colors"
          style={{
            height: 32,
            padding: '0 14px',
            borderRadius: 8,
            background: 'hsl(var(--teal))',
            color: 'hsl(var(--slate-navy))',
            border: '1px solid hsl(var(--teal))',
            fontSize: 12.5,
            fontWeight: 500,
            textDecoration: 'none',
          }}
        >
          Go home
        </Link>
      </div>
    </div>
  );
}

export default CompletePage;
